import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  HStack,
  VStack,
  Flex,
  Badge,
  Button,
  Divider,
  List,
  ListItem,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Avatar,
  Card,
  CardHeader,
  CardBody,
  Tooltip,
  Spinner,
  Progress,
  Tag,
  TagLabel,
  TagLeftIcon,
  Icon,
  Checkbox,
} from '@chakra-ui/react';
import { 
  FiEdit, 
  FiSend, 
  FiArrowLeft,
  FiPrinter,
  FiUser,
  FiMapPin,
  FiMail,
  FiPhone,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiStar,
  FiEye,
  FiFileText,
} from 'react-icons/fi';
// FiDownload is used for per-document download actions
import { FiDownload } from 'react-icons/fi';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import candidateService from '../../services/api/candidateService';
import * as committeeService from '../../services/api/committeeService';
import { useAuth } from '../../context/AuthContext';
import config from '../../config/config';
import reportService from '../../services/api/reportService';
// API config is now used through reportService
import { getNominationStatusLabel, getNominationStatusColor, normalizeNominationStatus } from '../../utils/evaluationStatusUtils';

// Import form components
import PersonalInfoForm from '../../components/forms/PersonalInfoForm';
import NominationForm from '../../components/forms/NominationForm';
import DocumentsForm from '../../components/forms/DocumentsForm';

/**
 * CandidateProfile component
 * Admin page for viewing detailed candidate information and managing their application
 */
const CandidateProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  
  // Determine if user is admin or committee member
  const isCommittee = user?.role === 'committee' || location.pathname.includes('/committee/');
  const isAdmin = user?.role === 'admin' || location.pathname.includes('/admin/');
  
  // State
  const [candidate, setCandidate] = useState(null);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [selectedCommitteeMembers, setSelectedCommitteeMembers] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  
  // Report options state
  const [includeEvaluation, setIncludeEvaluation] = useState(false);
  const [includeAttachmentA, setIncludeAttachmentA] = useState(false);
  const [includeAttachmentB, setIncludeAttachmentB] = useState(false);
  const [reportFormat, setReportFormat] = useState('pdf');
  const [downloadingReport, setDownloadingReport] = useState(false);
  
  // Helper: format date as YYYY-MM-DD
  const formatYMD = (raw) => {
    if (!raw) return 'غير متوفر';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      // Fallback: try to slice if it's already a string with time
      return String(raw).slice(0, 10);
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  
  // Fetch candidate data and committee members
  const fetchCandidateData = useCallback(async () => {
    setIsLoading(true);
    try {
      
      // Fetch candidate details using the appropriate service based on user role
      let response;
      if (isCommittee) {
        response = await committeeService.getCandidateForEvaluation(id);
      } else {
        response = await candidateService.getCandidateById(id);
      }
      
      if (response && response.data) {
        const candidateData = response.data;
        
        // Calculate age from birthdate
        if (candidateData.birthDate || candidateData.birth_date) {
          const birthDate = new Date(candidateData.birthDate || candidateData.birth_date);
          const ageDifMs = Date.now() - birthDate.getTime();
          const ageDate = new Date(ageDifMs);
          candidateData.age = Math.abs(ageDate.getUTCFullYear() - 1970);
        }
        
        // Use branch and center info directly from API response if available
        if (candidateData.branch && candidateData.branch.name) {
          candidateData.branchName = candidateData.branch.name;
          candidateData.branch_id = candidateData.branch.id;
        } else {
          // Fallback to looking up from config
          const branchId = candidateData.branchId || candidateData.branch_id;
          if (branchId) {
            const branch = config.organization.branches.find(b => b.id === branchId);
            candidateData.branchName = branch ? branch.name : 'غير معروف';
          }
        }
        
        if (candidateData.center && candidateData.center.name) {
          candidateData.centerName = candidateData.center.name;
          candidateData.center_id = candidateData.center.id;
        } else {
          // Fallback to looking up from config
          const centerId = candidateData.centerId || candidateData.center_id;
          if (centerId) {
            const center = config.organization.centers.find(c => c.id === centerId);
            candidateData.centerName = center ? center.name : 'غير معروف';
          }
        }
        
        // Normalize field names to ensure consistent access
        candidateData.firstName = candidateData.firstName || candidateData.first_name;
        candidateData.lastName = candidateData.lastName || candidateData.last_name;
        candidateData.fatherName = candidateData.fatherName || candidateData.father_name;
        candidateData.email = candidateData.email || candidateData.email_address;
        candidateData.phone = candidateData.phone || candidateData.phone_number;
        candidateData.nationalId = candidateData.nationalId || candidateData.national_id;
        candidateData.submissionDate = candidateData.submissionDate || candidateData.submission_date;
        candidateData.application_number = candidateData.application_number || candidateData.applicationNumber;
        
        // Ensure work information fields are properly normalized
        // We'll use the snake_case versions as the source of truth since they come from the backend
        if (candidateData.isCurrentlyEmployed === undefined && candidateData.is_currently_employed !== undefined) {
          candidateData.isCurrentlyEmployed = candidateData.is_currently_employed;
        }
        if (candidateData.hasNonProfitExperience === undefined && candidateData.has_nonprofit_experience !== undefined) {
          candidateData.hasNonProfitExperience = candidateData.has_nonprofit_experience;
        }
        
        setCandidate(candidateData);
        setFormData(candidateData);
        
        // Mock committee members data
        // TODO: Replace with actual API call when committee service is implemented
        setCommitteeMembers([
          { id: 1, name: 'أحمد محمد', email: 'ahmed@example.com', role: 'رئيس اللجنة' },
          { id: 2, name: 'فاطمة علي', email: 'fatima@example.com', role: 'عضو لجنة' },
          { id: 3, name: 'خالد عبدالله', email: 'khalid@example.com', role: 'عضو لجنة' },
          { id: 4, name: 'نورة سعد', email: 'noura@example.com', role: 'عضو لجنة' },
        ]);
      } else {
        throw new Error('Failed to fetch candidate data');
      }
    } catch (error) {
      console.error('Error fetching candidate data:', error);
      toast({
        title: 'خطأ في جلب البيانات',
        description: 'حدث خطأ أثناء محاولة جلب بيانات المرشح',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, toast, isCommittee]);
  
  // Fetch candidate data on component mount
  useEffect(() => {
    fetchCandidateData();
  }, [fetchCandidateData]);
  
  // Update candidate status
  const updateStatus = async () => {
    setStatusModalOpen(false);
    setIsLoading(true);
    
    try {
      const response = await candidateService.updateCandidateStatus(id, newStatus, statusComment);
      
      if (response && response.data) {
        // Update local state with new status
        setCandidate(prev => ({
          ...prev,
          status: newStatus,
          statusNotes: statusComment || prev.statusNotes
        }));
        
        toast({
          title: 'تم تحديث الحالة',
          description: `تم تغيير حالة المرشح إلى ${getNominationStatusLabel(newStatus)}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'خطأ في تحديث الحالة',
        description: 'حدث خطأ أثناء محاولة تحديث حالة المرشح',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setStatusComment('');
    }
  };
  
  // Assign committee members
  const assignCommitteeMembers = async () => {
    if (selectedCommitteeMembers.length === 0) {
      toast({
        title: 'تحذير',
        description: 'يرجى اختيار عضو لجنة واحد على الأقل',
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    
    try {
      // Note: This is a placeholder until we implement committee assignment in candidateService
      // For now, we'll just simulate a successful response
      // TODO: Implement assignCommittee method in candidateService
      
      // Mock successful response
      const response = { data: { success: true } };
      
      if (response.data && response.data.success) {
        toast({
          title: 'تم التعيين',
          description: 'تم تعيين أعضاء اللجنة بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        
        // Update local state if needed
        fetchCandidateData();
        
        // Reset and close modal
        setSelectedCommitteeMembers([]);
        setAssignModalOpen(false);
      } else {
        throw new Error('Failed to assign committee members');
      }
    } catch (error) {
      console.error('Error assigning committee members:', error);
      toast({
        title: 'خطأ في التعيين',
        description: 'حدث خطأ أثناء محاولة تعيين أعضاء اللجنة',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      setAssignModalOpen(false);
    }
  };
  
  // Handle form data changes
  const handleFormChange = (data, isValid) => {
    setFormData(prevData => ({
      ...prevData,
      ...data
    }));
  };
  
  // Save candidate data
  const saveCandidate = async () => {
    try {
      const response = await candidateService.updateCandidate(id, formData);
      
      if (response && response.data) {
        toast({
          title: 'تم الحفظ',
          description: 'تم حفظ بيانات المرشح بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        
        setEditMode(false);
        fetchCandidateData(); // Refresh data
      } else {
        throw new Error('Failed to save candidate data');
      }
    } catch (error) {
      console.error('Error saving candidate data:', error);
      toast({
        title: 'خطأ في الحفظ',
        description: 'حدث خطأ أثناء محاولة حفظ بيانات المرشح',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };
  
  // Print: open standard report PDF in a new tab and trigger browser print dialog (no attachments)
  const printProfile = async () => {
    setDownloadingReport(true);
    try {
      const response = await reportService.downloadNominationReport(id, {
        includeEvaluation: true,
        includeAttachmentA: false,
        includeAttachmentB: false,
        format: 'pdf'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        // Keep loading until the new tab is ready, then close loading
        const onLoad = () => {
          try { printWindow.focus(); printWindow.print(); } catch (e) { /* noop */ }
          setDownloadingReport(false);
        };
        // Some browsers support load on window, some on document
        printWindow.onload = onLoad;
        // Fallback: if onload doesn't fire, hide after a short grace period
        setTimeout(() => setDownloadingReport(false), 2500);
      } else {
        // Popup blocked or failed to open
        toast({ title: 'تم حظر نافذة الطباعة', description: 'يرجى السماح بالنوافذ المنبثقة والمحاولة مجددًا', status: 'warning', duration: 5000, isClosable: true });
        setDownloadingReport(false);
      }
    } catch (error) {
      console.error('Error preparing print:', error);
      toast({ title: 'تعذر فتح نافذة الطباعة', status: 'error', duration: 5000, isClosable: true });
      setDownloadingReport(false);
    }
  };
  
  // Download nomination report
  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      // Use the reportService to download the nomination report
      const response = await reportService.downloadNominationReport(id, {
        includeEvaluation,
        includeAttachmentA,
        includeAttachmentB,
        format: reportFormat
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: reportFormat === 'pdf' ? 'application/pdf' : 'application/zip' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Set filename based on format
      const extension = reportFormat === 'pdf' ? 'pdf' : 'zip';
      const candidateName = candidate?.candidate?.full_name || 
                           `${candidate?.firstName || ''} ${candidate?.lastName || ''}`;
      link.download = `${candidateName.replace(/\s+/g, '_')}_report.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'تم تنزيل التقرير بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Close modal after successful download
      setReportModalOpen(false);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast({
        title: 'حدث خطأ أثناء تنزيل التقرير',
        description: 'يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadingReport(false);
    }
  };
  
  // Handle back button click
  const handleBack = () => {
    // Navigate back based on user role
    if (isCommittee) {
      navigate('/committee/candidates');
    } else {
      navigate('/admin/candidates');
    }
  };
  
  // Centralized helpers
  const getStatusColorScheme = (status) => getNominationStatusColor(status);
  const getStatusLabel = (status) => getNominationStatusLabel(status);
  
  // Loading state
  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text textAlign="center" fontSize="lg">جاري تحميل البيانات...</Text>
      </Container>
    );
  }
  
  // Error state
  if (!candidate) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text textAlign="center" fontSize="lg" mb={4}>لم يتم العثور على بيانات المرشح</Text>
        <Flex justify="center">
          <Button 
            leftIcon={<FiArrowLeft />} 
            colorScheme="blue"
            onClick={() => navigate('/admin/candidates')}
          >
            العودة إلى قائمة المرشحين
          </Button>
        </Flex>
      </Container>
    );
  }
  
  // Edit mode - show form components
  if (editMode) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="space-between" align="center" mb={8}>
          <Button 
            leftIcon={<FiArrowLeft />} 
            variant="outline" 
            onClick={() => setEditMode(false)}
          >
            إلغاء التعديل
          </Button>
          
          <Button 
            leftIcon={<FiCheckCircle />} 
            colorScheme="green"
            onClick={saveCandidate}
          >
            حفظ التغييرات
          </Button>
        </Flex>
        
        <VStack spacing={8} align="stretch">
          <Box>
            <PersonalInfoForm 
              formData={formData} 
              onChange={handleFormChange} 
              showValidation={false}
            />
          </Box>
          
          {/* Education and Experience forms removed as requested */}
          
          <Box>
            <NominationForm 
              formData={formData} 
              onChange={handleFormChange} 
              showValidation={false}
            />
          </Box>
          
          <Box>
            <DocumentsForm 
              formData={formData} 
              onChange={handleFormChange} 
              showValidation={false}
              readOnly={true}
            />
          </Box>
        </VStack>
      </Container>
    );
  }
  
  // Main render - CV layout
  return (
    <Container maxW="container.xl" py={8}>
      {/* Header with action buttons */}
      <Flex justify="space-between" align="center" mb={8}>
        <Button 
          leftIcon={<FiArrowLeft />} 
          variant="outline" 
          onClick={handleBack}
        >
          العودة إلى القائمة
        </Button>
        
        <Flex gap={2}>
          {/* Removed تنزيل الطلب as it's redundant with the report */}
          <Tooltip label="تنزيل تقرير المرشح" placement="top">
            <Button 
              leftIcon={<FiFileText />} 
              colorScheme="green" 
              variant="outline"
              onClick={() => setReportModalOpen(true)}
            >
              تنزيل التقرير
            </Button>
          </Tooltip>
          <Tooltip label="طباعة الملف" placement="top">
            <Button 
              leftIcon={<FiPrinter />} 
              colorScheme="blue" 
              variant="outline"
              onClick={printProfile}
            >
              طباعة
            </Button>
          </Tooltip>
          
          {/* Admin-only buttons */}
          {isAdmin && (
            <>
              <Tooltip label="تعديل البيانات" placement="top">
                <Button 
                  leftIcon={<FiEdit />} 
                  colorScheme="blue" 
                  variant="outline"
                  onClick={() => setEditMode(true)}
                >
                  تعديل
                </Button>
              </Tooltip>
              <Tooltip label="تعيين للمراجعة" placement="top">
                <Button 
                  leftIcon={<FiSend />} 
                  colorScheme="green" 
                  variant="outline"
                  onClick={() => setAssignModalOpen(true)}
                >
                  تعيين للمراجعة
                </Button>
              </Tooltip>
              <Tooltip label="تغيير الحالة" placement="top">
                <Button 
                  leftIcon={<FiEdit />} 
                  colorScheme="orange"
                  onClick={() => setStatusModalOpen(true)}
                >
                  تغيير الحالة
                </Button>
              </Tooltip>
            </>
          )}
          
          {/* Committee-only buttons */}
          {isCommittee && !candidate.is_evaluated && (
            <Tooltip label="تقييم المرشح" placement="top">
              <Button 
                leftIcon={<FiStar />} 
                colorScheme="green"
                onClick={() => navigate(`/committee/evaluate/${id}`)}
              >
                تقييم المرشح
              </Button>
            </Tooltip>
          )}
        </Flex>
      </Flex>
      {downloadingReport && (
        <Modal isOpen={true} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false}>
          <ModalOverlay />
          <ModalContent>
            <ModalBody py={8} textAlign="center">
              <Spinner size="xl" thickness="4px" />
              <Text mt={4}>جاري تجهيز الملف...</Text>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      
      {/* CV Layout */}
      <Grid templateColumns="repeat(12, 1fr)" gap={6}>
        {/* Left column - Personal info and status */}
        <GridItem colSpan={{ base: 12, md: 4 }}>
          <VStack spacing={6} align="stretch">
            {/* Personal Information Card */}
            <Card>
              <CardHeader>
                <Heading size="md">المعلومات الشخصية</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                <Flex align="center" gap={4}>
            {candidate.personal_photo_url ? (
              <Avatar 
                size="2xl" 
                src={candidate.personal_photo_url} 
                name={candidate.first_name + ' ' + candidate.last_name}
              />
            ) : (
              <Avatar 
                size="2xl" 
                name={candidate.first_name + ' ' + candidate.last_name}
                bg="gray.300"
                icon={<FiUser fontSize="2rem" />}
              />
            )}
            <Box>
              <Heading size="md" mb={2}>
                {[
                  candidate.first_name || candidate.firstName,
                  candidate.father_name || candidate.fatherName,
                  candidate.last_name || candidate.lastName,
                ].filter(Boolean).join(' ')}
              </Heading>
              <Text color="gray.600" fontSize="md" mb={1}>
                رقم الطلب: {candidate.application_number || 'غير متوفر'}
              </Text>
              <Badge 
                colorScheme={getStatusColorScheme(candidate.status)} 
                fontSize="md" 
                px={3} 
                py={1}
              >
                {getStatusLabel(candidate.status)}
              </Badge>
            </Box>
          </Flex>
                  
                  <Divider />
                  
                  <HStack>
                    <FiMail />
                    <Text>{candidate.email}</Text>
                  </HStack>
                  
                  <HStack>
                    <FiPhone />
                    <Text>{candidate.phone}</Text>
                  </HStack>

                  <HStack>
                    <FiUser />
                    <Text>رقم الهوية: {candidate.national_id}</Text>
                  </HStack>
                  
                  <HStack>
                    <FiCalendar />
                    <Text>
                      تاريخ الميلاد: {formatYMD(candidate.birth_date || candidate.birthDate)} {candidate.age && `(${candidate.age} سنة)`}
                    </Text>
                  </HStack>

                  <HStack>
                    <FiCalendar />
                    <Text>تاريخ التقديم: {new Date(candidate.submission_date).toLocaleDateString('ar-SA')}</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Address Information Card */}
            <Card>
              <CardHeader>
                <Heading size="md">معلومات العنوان</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack>
                    <FiMapPin />
                    <Text>المدينة: {candidate.city}</Text>
                  </HStack>
                  
                  <HStack>
                    <FiMapPin />
                    <Text>الحي: {candidate.district}</Text>
                  </HStack>
                  
                  <HStack>
                    <FiMapPin />
                    <Text>الشارع: {candidate.street}</Text>
                  </HStack>

                  <HStack>
                    <FiMapPin />
                    <Text>رقم المبنى: {candidate.building_number}</Text>
                  </HStack>

                  <HStack>
                    <FiMapPin />
                    <Text>الرمز البريدي: {candidate.postal_code}</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
            
            {/* Membership Information */}
            <Card>
              <CardHeader>
                <Heading size="md">معلومات العضوية</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack>
                    <FiUser />
                    <Text>حالة العضوية: {candidate.is_member ? 'عضو في 2025' : 'غير عضو في 2025'}</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
            
            {/* Work Information */}
            <Card>
              <CardHeader>
                <Heading size="md">معلومات العمل</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  {candidate.is_currently_employed ? (
                    <>
                      <HStack>
                        <FiUser />
                        <Text>جهة العمل: {candidate.employer || 'غير متوفر'}</Text>
                      </HStack>
                      <HStack>
                        <FiUser />
                        <Text>المسمى الوظيفي: {candidate.job_title || 'غير متوفر'}</Text>
                      </HStack>
                    </>
                  ) : (
                    <Text>غير موظف حالياً</Text>
                  )}
                  
                  {candidate.has_nonprofit_experience && (
                    <>
                      <Divider />
                      <Text fontWeight="bold">خبرة في القطاع غير الربحي:</Text>
                      <HStack>
                        <FiUser />
                        <Text>المنظمة: {candidate.nonprofit_organization || 'غير متوفر'}</Text>
                      </HStack>
                      <HStack>
                        <FiUser />
                        <Text>سنوات الخبرة: {candidate.years_of_experience || 'غير متوفر'}</Text>
                      </HStack>
                    </>
                  )}
                </VStack>
              </CardBody>
            </Card>
            
            {/* Application Status Card */}
            <Card>
              <CardHeader>
                <Heading size="md">حالة الطلب</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Flex justify="space-between" align="center">
                    <Badge colorScheme={getStatusColorScheme(candidate.status)} fontSize="md" px={2} py={1}>
                      {getStatusLabel(candidate.status)}
                    </Badge>
                    <Text fontSize="sm">تاريخ التحديث: {candidate.updated_at ? new Date(candidate.updated_at).toLocaleDateString() : 'غير متوفر'}</Text>
                  </Flex>
                  
                  <Box>
                    <Text fontSize="sm" mb={2}>مراحل الطلب</Text>
                    <Progress 
                      value={(() => {
                        const s = normalizeNominationStatus(candidate.status);
                        if (s === 'draft') return 0;
                        if (s === 'submitted') return 25;
                        if (s === 'pending') return 50;
                        if (s === 'shortlisted') return 75;
                        if (s === 'accepted' || s === 'rejected' || s === 'returned') return 100;
                        return 25;
                      })()} 
                      colorScheme={candidate.status === 'rejected' ? 'red' : 'green'}
                      size="sm"
                      mb={2}
                    />
                    <Flex justify="space-between" fontSize="xs" color="gray.500">
                      <Text>تقديم</Text>
                      <Text>بانتظار التقييم</Text>
                      <Text>في انتظار النتيجة</Text>
                    </Flex>
                  </Box>
                  
                  {candidate.statusComment && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="bold">ملاحظات:</Text>
                      <Text fontSize="sm">{candidate.statusComment}</Text>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </GridItem>
        
        {/* Right column - Nomination details, documents, evaluations */}
        <GridItem colSpan={{ base: 12, md: 8 }}>
          <VStack spacing={6} align="stretch">
            {/* Nomination Details Card */}
            <Card>
              <CardHeader>
                <Heading size="md">تفاصيل الترشيح</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    <GridItem>
                      <Text fontWeight="bold">الفرع:</Text>
                      <Text>{candidate.branchName || candidate.branch}</Text>
                    </GridItem>
                    
                    <GridItem>
                      <Text fontWeight="bold">المركز:</Text>
                      <Text>{candidate.centerName || (candidate.center && typeof candidate.center === 'object' ? candidate.center.name : candidate.center || 'غير معروف')}</Text>
                    </GridItem>
                  </Grid>

                  <Divider />
                  
                  <Box>
                    <Text fontWeight="bold" mb={2}>معرفة الجمعية:</Text>
                    <Text>{candidate.society_knowledge}</Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>المسؤولية الاجتماعية:</Text>
                    <Text>{candidate.social_responsibility_contribution}</Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>برامج رؤية 2030:</Text>
                    <Text>{candidate.vision_2030_contribution}</Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>أفكار لزيادة دخل المركز:</Text>
                    <Text>{candidate.center_income_ideas}</Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>الإقرارات:</Text>
                    <HStack>
                      <Badge colorScheme={candidate.regulations_acknowledgement ? 'green' : 'red'}>
                        {candidate.regulations_acknowledgement ? 'تم الاطلاع على اللوائح' : 'لم يتم الاطلاع على اللوائح'}
                      </Badge>
                      <Badge colorScheme={candidate.commitment_acknowledgement ? 'green' : 'red'}>
                        {candidate.commitment_acknowledgement ? 'تم تأكيد المسؤوليات' : 'لم يتم تأكيد المسؤوليات'}
                      </Badge>
                      <Badge colorScheme={candidate.terms_and_conditions_acknowledgement ? 'green' : 'red'}>
                        {candidate.terms_and_conditions_acknowledgement ? 'تم قبول الشروط والأحكام' : 'لم يتم قبول الشروط والأحكام'}
                      </Badge>
                    </HStack>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
            
            {/* Documents Card */}
            <Card>
              <CardHeader>
                <Heading size="md">المستندات المرفقة</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  {/* Security Clearance */}
                  {candidate.security_clearance_document_url && (
                    <Box>
                      <Text fontWeight="bold">المسح الأمني</Text>
                      <HStack mt={2} spacing={2}>
                        <Badge
                          p={2}
                          borderRadius="md"
                          colorScheme="blue"
                          variant="outline"
                          cursor="pointer"
                          onClick={() => window.open(candidate.security_clearance_document_url, '_blank')}
                        >
                          <HStack>
                            <Icon as={FiEye} />
                            <Text>المسح الأمني - {String(candidate.application_number).slice(-5)}</Text>
                          </HStack>
                        </Badge>
                        <Button 
                          size="sm" 
                          leftIcon={<FiDownload />} 
                          colorScheme="blue" 
                          variant="ghost"
                          onClick={() => window.open(candidate.security_clearance_document_url, '_blank')}
                        >
                          تنزيل
                        </Button>
                      </HStack>
                    </Box>
                  )}

                  {/* CV */}
                  {candidate.cv_document_url && (
                    <Box>
                      <Text fontWeight="bold">السيرة الذاتية</Text>
                      <HStack mt={2} spacing={2}>
                        <Badge
                          p={2}
                          borderRadius="md"
                          colorScheme="blue"
                          variant="outline"
                          cursor="pointer"
                          onClick={() => window.open(candidate.cv_document_url, '_blank')}
                        >
                          <HStack>
                            <Icon as={FiEye} />
                            <Text>سيرة ذاتية - {String(candidate.application_number).slice(-5)}</Text>
                          </HStack>
                        </Badge>
                        <Button 
                          size="sm" 
                          leftIcon={<FiDownload />} 
                          colorScheme="blue" 
                          variant="ghost"
                          onClick={() => window.open(candidate.cv_document_url, '_blank')}
                        >
                          تنزيل
                        </Button>
                      </HStack>
                    </Box>
                  )}
                  
                  {/* Other Documents */}
                  {candidate.additional_documents && (() => {
                    let parsedDocs = [];
                    try {
                      // If it's a string (JSON), parse it
                      if (typeof candidate.additional_documents === 'string') {
                        parsedDocs = JSON.parse(candidate.additional_documents);
                      } 
                      // If it's already an array, use it directly
                      else if (Array.isArray(candidate.additional_documents)) {
                        parsedDocs = candidate.additional_documents;
                      }
                    } catch (e) {
                      console.error('Error parsing additional documents:', e);
                    }
                    return parsedDocs.map((doc, index) => (
                    <Box key={index}>
                      <Text fontWeight="bold">{doc.name}</Text>
                      <Text fontSize="sm" color="gray.500">{doc.type}</Text>
                      <HStack mt={2} spacing={2}>
                        <Badge
                          p={2}
                          borderRadius="md"
                          colorScheme="blue"
                          variant="outline"
                          cursor="pointer"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          <HStack>
                            <Icon as={FiEye} />
                            <Text>{(doc.name || doc.type || 'مستند إضافي') + ' - ' + String(candidate.application_number).slice(-5)}</Text>
                          </HStack>
                        </Badge>
                        <Button 
                          size="sm" 
                          leftIcon={<FiDownload />} 
                          colorScheme="blue" 
                          variant="ghost"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          تنزيل
                        </Button>
                      </HStack>
                    </Box>
                    ));
                  })()}
                  
                  {(() => {
                    // Check if there are any documents
                    const hasPhotoDoc = !!candidate.personal_photo_url;
                    const hasSecurityDoc = !!candidate.security_clearance_document_url;
                    const hasCvDoc = !!candidate.cv_document_url;
                    
                    // Check additional documents
                    let hasAdditionalDocs = false;
                    if (candidate.additional_documents) {
                      try {
                        const docs = typeof candidate.additional_documents === 'string' 
                          ? JSON.parse(candidate.additional_documents) 
                          : candidate.additional_documents;
                        hasAdditionalDocs = Array.isArray(docs) && docs.length > 0;
                      } catch (e) {
                        console.error('Error checking additional documents:', e);
                      }
                    }
                    
                    // Show message if no documents are available
                    if (!hasPhotoDoc && !hasSecurityDoc && !hasCvDoc && !hasAdditionalDocs) {
                      return <Text>لا توجد مستندات مرفقة</Text>;
                    }
                    return null;
                  })()}
                </VStack>
              </CardBody>
            </Card>
            
            {/* Evaluations Card */}
            <Card>
              <CardHeader>
                <Heading size="md">تقييمات اللجنة</Heading>
              </CardHeader>
              <CardBody>
                {candidate.evaluations && candidate.evaluations.length > 0 ? (
                  <VStack spacing={4} align="stretch">
                    {candidate.evaluations.map((evaluation, index) => (
                      <Card key={index} variant="outline">
                        <CardBody>
                          <Flex justify="space-between" align="center" mb={2}>
                            <HStack>
                              <Avatar size="sm" name={evaluation.evaluator?.full_name || 'مقيم'} />
                              <Text fontWeight="bold">{evaluation.evaluator?.full_name || `مقيم #${index + 1}`}</Text>
                            </HStack>
                            <Badge colorScheme={evaluation.overall_score >= 7 ? 'green' : evaluation.overall_score >= 5 ? 'yellow' : 'red'}>
                              {evaluation.overall_score ? Number(evaluation.overall_score).toFixed(1) : 0}/10
                            </Badge>
                          </Flex>
                          <Text>{evaluation.comments || 'لا توجد ملاحظات'}</Text>
                          <Text fontSize="sm" color="gray.500" mt={2}>
                            {evaluation.updated_at ? new Date(evaluation.updated_at).toLocaleDateString() : 'تاريخ غير محدد'}
                          </Text>
                        </CardBody>
                      </Card>
                    ))}
                  </VStack>
                ) : (
                  <Text>لم يتم تقييم المرشح بعد</Text>
                )}
              </CardBody>
            </Card>
            
            {/* Activity Log Card */}
            <Card>
              <CardHeader>
                <Heading size="md">سجل النشاط</Heading>
              </CardHeader>
              <CardBody>
                {candidate.activityLog && candidate.activityLog.length > 0 ? (
                  <VStack spacing={3} align="stretch">
                    {candidate.activityLog.map((activity, index) => (
                      <Flex key={index} gap={3}>
                        <Box>
                          <Tag size="md" variant="subtle" colorScheme="blue">
                            <TagLeftIcon as={FiClock} />
                            <TagLabel>{new Date(activity.date).toLocaleDateString('ar-SA')}</TagLabel>
                          </Tag>
                        </Box>
                        <Box flex="1">
                          <Text fontWeight="bold">{activity.action}</Text>
                          <Text fontSize="sm">{activity.description}</Text>
                          <Text fontSize="xs" color="gray.500">{activity.user}</Text>
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                ) : (
                  <Text>لا يوجد نشاط مسجل</Text>
                )}
              </CardBody>
            </Card>
          </VStack>
        </GridItem>
      </Grid>
      
      {/* Status Update Modal */}
      <Modal isOpen={statusModalOpen} onClose={() => setStatusModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تغيير حالة المرشح</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>الحالة</FormLabel>
                <Select 
                  value={newStatus} 
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="draft">مسودة</option>
                  <option value="submitted">تحت التقييم</option>
                  <option value="pending">في انتظار النتيجة</option>
                  <option value="shortlisted">قبول أولي</option>
                  <option value="accepted">مقبول</option>
                  <option value="rejected">مرفوض</option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>ملاحظات</FormLabel>
                <Textarea 
                  value={statusComment} 
                  onChange={(e) => setStatusComment(e.target.value)}
                  placeholder="أضف ملاحظات حول سبب تغيير الحالة"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setStatusModalOpen(false)}>
              إلغاء
            </Button>
            <Button colorScheme="blue" onClick={updateStatus}>
              حفظ
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Committee Assignment Modal */}
      <Modal isOpen={assignModalOpen} onClose={() => setAssignModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تعيين أعضاء اللجنة</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>اختر أعضاء اللجنة</FormLabel>
              <Select 
                placeholder="اختر أعضاء اللجنة"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value && !selectedCommitteeMembers.includes(value)) {
                    setSelectedCommitteeMembers([...selectedCommitteeMembers, value]);
                  }
                }}
              >
                {committeeMembers.map((member) => (
                  <option key={member.id} value={member.id} disabled={selectedCommitteeMembers.includes(member.id)}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            
            <Box mt={4}>
              <Text fontWeight="bold" mb={2}>الأعضاء المختارون:</Text>
              {selectedCommitteeMembers.length > 0 ? (
                <List spacing={2}>
                  {selectedCommitteeMembers.map((memberId) => {
                    const member = committeeMembers.find(m => m.id === memberId);
                    return (
                      <ListItem key={memberId}>
                        <Flex justify="space-between" align="center">
                          <Text>{member?.name}</Text>
                          <IconButton 
                            size="sm"
                            icon={<FiXCircle />}
                            aria-label="إزالة"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => setSelectedCommitteeMembers(selectedCommitteeMembers.filter(id => id !== memberId))}
                          />
                        </Flex>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Text color="gray.500">لم يتم اختيار أي عضو بعد</Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => {
              setAssignModalOpen(false);
              setSelectedCommitteeMembers([]);
            }}>
              إلغاء
            </Button>
            <Button colorScheme="blue" onClick={assignCommitteeMembers}>
              تعيين
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Report Options Modal */}
      <Modal isOpen={reportModalOpen} onClose={() => setReportModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>خيارات التقرير</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>حدد محتويات التقرير:</Text>
              
              <Checkbox 
                isChecked={includeEvaluation} 
                onChange={(e) => setIncludeEvaluation(e.target.checked)}
              >
                تضمين تقرير التقييم
              </Checkbox>
              
              <Checkbox 
                isChecked={includeAttachmentA} 
                onChange={(e) => setIncludeAttachmentA(e.target.checked)}
              >
                تضمين السيرة الذاتية
              </Checkbox>
              
              <Checkbox 
                isChecked={includeAttachmentB} 
                onChange={(e) => setIncludeAttachmentB(e.target.checked)}
              >
                تضمين المسح الأمني
              </Checkbox>
              
              <FormControl>
                <FormLabel>تنسيق التقرير</FormLabel>
                <Select
                  value={reportFormat}
                  onChange={(e) => setReportFormat(e.target.value)}
                >
                  <option value="pdf">ملف PDF واحد (مدمج)</option>
                  <option value="zip">ملفات منفصلة (مضغوطة)</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={() => setReportModalOpen(false)}>
              إلغاء
            </Button>
            <Button 
              colorScheme="green" 
              leftIcon={<FiDownload />}
              onClick={downloadReport}
              isLoading={downloadingReport}
              loadingText="جاري التنزيل..."
            >
              تنزيل التقرير
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CandidateProfile;
